import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import { COMPANY } from "@/lib/siteData";

const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_fitzgerald-earth/artifacts/kiawg6az_492098098_1238228484977730_6981482330082763425_n.jpg";

const VALUES = [
  {
    title: "Quality Workmanship",
    text: "We don't cut corners. Levels are right, drainage falls work, and the platform's locked in before we leave.",
  },
  {
    title: "Practical Solutions",
    text: "Two decades on the ground means we know what works in Waikato clay, wet country, and tight residential blocks.",
  },
  {
    title: "Straight Talking",
    text: "Plain-English quotes, honest timelines, and no surprises when the invoice lands.",
  },
  {
    title: "Family Values",
    text: "A family-run business takes pride in its name. Every job we put our name on, we stand behind.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="— About Us"
        title="Two decades of practical earthworks across Waikato, Auckland & Hauraki."
        intro="Fitzgerald Earthworks has been proudly operating from Mangatangi for years. As a family-run business with 20+ years in the industry, we understand the importance of dependable service, strong relationships, and quality results."
      />

      <section className="section-pad bg-[#F6F5F2]">
        <div className="container-fz grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <img
              src={ABOUT_IMG}
              alt="Fitzgerald Earthworks Ltd site cut in progress"
              className="w-full aspect-[4/5] object-cover border border-[#D2D0C9]"
            />
          </motion.div>
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">— Our story</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-stone-900 leading-[1.05]">
              Built on relationships, run by the people doing the work.
            </h2>
            <div className="mt-8 space-y-5 text-base md:text-lg text-stone-700 leading-relaxed">
              <p>
                We started out shifting dirt for friends, family and local farmers
                — and twenty-plus years on, that hasn't really changed. A lot of
                our work still comes from word of mouth, repeat clients and the
                handshake culture rural New Zealand was built on.
              </p>
              <p>
                From farm projects and rural infrastructure to residential site
                works and drainage, we bring local knowledge, modern equipment,
                and a commitment to completing every project safely and
                efficiently.
              </p>
              <p>
                Whether it's a stock water pond, a new house platform, or a
                kilometre of farm race, we'd be glad to talk it through.
              </p>
            </div>

            <div className="mt-10">
              <Link to="/contact" data-testid="about-contact-link" className="btn-primary">
                Talk to us <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-[#D2D0C9] bg-[#F6F5F2]">
        <div className="container-fz py-20 md:py-28">
          <p className="eyebrow mb-6">— What we stand for</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-stone-900 max-w-3xl leading-tight">
            Four things we never compromise on.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#D2D0C9] border border-[#D2D0C9]">
            {VALUES.map((v, i) => (
              <div key={v.title} className="bg-[#F6F5F2] p-8 md:p-12">
                <span className="num-marker">0{i + 1} — Value</span>
                <h3 className="mt-4 font-display text-2xl md:text-3xl text-stone-900">
                  {v.title}
                </h3>
                <p className="mt-4 text-stone-600 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1A1A18] text-[#F6F5F2]">
        <div className="container-fz py-20 md:py-24 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <p className="font-display text-5xl md:text-6xl text-[#D05322]">20+</p>
            <p className="mt-3 text-stone-300 uppercase tracking-[0.22em] text-xs">
              Years in the industry
            </p>
          </div>
          <div>
            <p className="font-display text-5xl md:text-6xl text-[#D05322]">100%</p>
            <p className="mt-3 text-stone-300 uppercase tracking-[0.22em] text-xs">
              Family-owned & operated
            </p>
          </div>
          <div>
            <p className="font-display text-5xl md:text-6xl text-[#D05322]">3</p>
            <p className="mt-3 text-stone-300 uppercase tracking-[0.22em] text-xs">
              Regions — {COMPANY.region}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
